import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, Building2, Search } from 'lucide-react';
import { selectCurrentGroupId, selectAllWorkspaces } from '@/store/selectors';
import { updateCurrentUserGroup as updateCurrentUserGroupForRedux } from '@/app/features/workspace/workspaceSlice';
import { getCurrentRootGroup } from '@/app/features/workspace/utils';
import type { Workspace } from '@/types/workspace';
import { cutItemTitle } from '@/util/FitTextIntoWidth';
import { NavDropdown } from './NavDropdown';

interface NavWorkspacesProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  notifications: unknown[];
}

interface WorkspaceItem {
  id: string;
  title: string;
  parent: string;
  image?: string;
  active: boolean;
  notifications: number;
}

interface WorkspaceSection {
  id: string;
  title: string;
  items: WorkspaceItem[];
}

export function NavWorkspaces({
  open,
  onToggle,
  onClose,
  notifications,
}: NavWorkspacesProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentUserGroup = useSelector(selectCurrentGroupId);
  const allWorkspaces = useSelector(selectAllWorkspaces) as Workspace[];

  const currentWorkspace = useMemo(
    () => allWorkspaces.find((w) => w.id === currentUserGroup),
    [allWorkspaces, currentUserGroup],
  );

  // Group workspaces into sections per root workspace, active root first,
  // plus "Other workspaces" for ones whose root the user can't see
  // (same rule as the workspace settings page)
  const sections = useMemo<WorkspaceSection[]>(() => {
    const decorate = (w: Workspace): WorkspaceItem => {
      const note = (notifications as Array<Record<string, number>>).find(
        (n) => n[w.id],
      );
      return {
        id: w.id,
        title: cutItemTitle(w.title, 130),
        parent: w.parent,
        image: w.image,
        active: currentUserGroup === w.id,
        notifications: note ? note[w.id] : 0,
      };
    };
    const byAttention = (a: WorkspaceItem, b: WorkspaceItem) => {
      if (a.notifications !== b.notifications) return b.notifications - a.notifications;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    };

    const roots = allWorkspaces.filter((w) => w.parent === 'root');
    const activeRoot = getCurrentRootGroup(allWorkspaces, currentUserGroup);

    const sortedRoots = [...roots].sort((a, b) => {
      if (a.id === activeRoot?.id) return -1;
      if (b.id === activeRoot?.id) return 1;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });

    const result: WorkspaceSection[] = sortedRoots.map((root) => ({
      id: root.id,
      title: root.title,
      items: [
        decorate(root),
        ...allWorkspaces
          .filter((w) => w.parent === root.id)
          .map(decorate)
          .sort(byAttention),
      ],
    }));

    const others = allWorkspaces
      .filter((w) => w.parent !== 'root' && !roots.some((r) => r.id === w.parent))
      .map(decorate)
      .sort(byAttention);

    if (others.length) {
      const othersSection: WorkspaceSection = {
        id: 'other-workspaces',
        title: 'Other workspaces',
        items: others,
      };
      // "current context first" also when the active workspace lives here
      if (others.some((o) => o.active)) result.unshift(othersSection);
      else result.push(othersSection);
    }

    return result;
  }, [notifications, allWorkspaces, currentUserGroup]);

  // Single-root users without foreign workspaces see today's flat list
  const showHeaders = sections.length > 1;

  const filteredSections = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter((w) => w.title.toLowerCase().includes(q)),
      }))
      .filter((s) => s.items.length > 0);
  }, [sections, searchTerm]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const switchWorkspace = (id: string) => {
    window.localStorage.setItem('currentUserGroup', id);
    dispatch(updateCurrentUserGroupForRedux(id));

    const target = allWorkspaces.find((w) => w.id === id);
    if (target?.parent === 'root') {
      navigate('/in/account/workspace');
    } else {
      navigate('/in/folder/root');
    }

    onClose();
  };

  const navigateTo = (url: string) => {
    navigate(url);
    onClose();
  };

  return (
    <NavDropdown
      open={open}
      onClose={onClose}
      onTriggerClick={onToggle}
      closeOnContentClick={false}
      align="right"
      className="w-80"
      trigger={
        <>
          <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded">
            {currentWorkspace?.image ? (
              <img
                src={currentWorkspace.image}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <span className="hidden max-w-[120px] truncate text-sm font-medium text-accent-foreground md:inline">
            {currentWorkspace?.title}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </>
      }
    >
      <button
        type="button"
        className="w-full px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-accent-foreground"
        onClick={() => navigateTo('/in/account/workspace')}
      >
        Edit workspaces
      </button>

      <div className="border-b border-border-light px-3 py-1.5">
        <div className="flex items-center gap-2 rounded-md bg-secondary px-2 py-1.5">
          <Search className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search workspaces..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {filteredSections.length === 0 && (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            No workspaces found
          </div>
        )}
        {filteredSections.map((section, sectionIdx) => (
          <div
            key={section.id}
            className={showHeaders && sectionIdx > 0 ? 'mt-1 border-t border-border-light' : ''}
          >
            {showHeaders && (
              <div className="truncate px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </div>
            )}
            {section.items.map((ws) => (
              <button
                key={ws.id}
                type="button"
                className={`flex w-full items-center gap-2.5 py-2 text-left transition-colors ${
                  showHeaders && ws.parent !== 'root' ? 'pl-6 pr-3' : 'px-3'
                } ${ws.active ? 'bg-primary-muted' : 'hover:bg-secondary'}`}
                onClick={() => switchWorkspace(ws.id)}
              >
                {ws.active && (
                  <div className="absolute left-0 h-5 w-0.5 rounded-r bg-primary" />
                )}
                <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded">
                  {ws.image ? (
                    <img src={ws.image} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <span className="min-w-0 flex-1 truncate text-sm text-accent-foreground">
                  {ws.title}
                </span>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {ws.parent === 'root' && (
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      Admin
                    </span>
                  )}
                  {ws.notifications > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-primary-foreground">
                      {ws.notifications > 99 ? '99' : ws.notifications}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      {filteredSections.reduce((n, s) => n + s.items.length, 0) > 6 && (
        <button
          type="button"
          className="w-full border-t border-border-light px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-accent-foreground"
          onClick={() => navigateTo('/in/account/workspace')}
        >
          Edit workspaces
        </button>
      )}
    </NavDropdown>
  );
}
